// NOTICE: KAPTION-INTEGRATION
//
// Walk a freshly-parsed ProseMirror doc and convert text-nodes-with-mention://-
// link-marks back into atomic `mention` nodes.
//
// Why: the markdown round-trip used by ReplyBox drafts and saved-content
// rehydration goes
//   atom mention → "[@Name](mention://user/X/Name)" → markdown-it parse
// and `@chatwoot/prosemirror-schema`'s parser does NOT register a
// markdown-it tokenizer that emits a `mention` token for these links.
// The doc rehydrates as text-with-link-mark, NOT as an atomic mention node.
//
// That breaks the suggestion plugin's prefix check (`/^[\s\0]?$/`) on the
// next `@` typed after such a text-link mention — the previous char becomes
// the last letter of the rehydrated name (e.g. `z` from "Lopez") instead of
// the `\0` that atomic nodes contribute via textBetween's leafText arg.
// Result: picker never reopens for the second mention after a reload.
//
// This helper is called once in Editor.vue#createState immediately after
// `MessageMarkdownTransformer.parse(content)` and restores the atomic
// representation. Vendored package stays untouched.

import { Fragment, Slice } from 'prosemirror-model';

const MENTION_HREF_PATTERN = /^mention:\/\/(user|team)\/(\d+)\/(.+)$/;

/**
 * @param {Node} doc — a ProseMirror Node (typically a freshly-parsed doc)
 * @param {Schema} schema — the message schema (must define `mention` node + `link` mark)
 * @returns {Node} doc with text+link-mark mention nodes converted to atomic mentions.
 *                 Returns the original doc if nothing to rewrite.
 */
export function rehydrateMentions(doc, schema) {
  // Some Editor.vue mount paths (test mounts with partial schemas, embeds that
  // disable mentions) call us with a schema that has no `nodes`/`marks` map.
  // Treat that as "nothing to rehydrate" rather than crashing.
  if (!schema?.nodes || !schema?.marks) return doc;
  const mentionType = schema.nodes.mention;
  const linkType = schema.marks.link;
  if (!mentionType || !linkType) return doc;

  // Collect positions in document order; apply replacements in reverse so
  // earlier positions stay valid as we mutate.
  const matches = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const linkMark = node.marks.find(
      m =>
        m.type === linkType &&
        typeof m.attrs?.href === 'string' &&
        m.attrs.href.startsWith('mention://')
    );
    if (linkMark) {
      matches.push({
        from: pos,
        to: pos + node.nodeSize,
        href: linkMark.attrs.href,
      });
    }
    return false;
  });

  if (matches.length === 0) return doc;

  let result = doc;
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const { from, to, href } = matches[i];
    const parsed = href.match(MENTION_HREF_PATTERN);
    if (parsed) {
      const [, kind, userId, encodedName] = parsed;
      let userFullName;
      try {
        userFullName = decodeURIComponent(encodedName);
      } catch (_e) {
        userFullName = encodedName;
      }

      const atom = mentionType.create({
        userId,
        userFullName,
        mentionType: kind,
      });

      result = result.replace(from, to, new Slice(Fragment.from(atom), 0, 0));
    }
  }

  return result;
}
